import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Target, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Dialog to pick which Google Ads "Conversion Action" (UPLOAD_CLICKS) the won leads
 * of this client should be pushed to. Required to close the Smart Bidding loop via
 * the Bonus offline-conversions feature.
 */
export default function OfflineConversionActionDialog({ open, onOpenChange, client, onSaved }) {
  const [actions, setActions] = useState([]);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !client) return;
    setError('');
    setLoading(true);
    axios.get(`${API}/google-ads/clients/${client.id}/conversion-actions`, { withCredentials: true })
      .then(({ data }) => {
        setActions(data.actions || []);
        setSelected(data.selected_action_id || '');
      })
      .catch((e) => setError(e.response?.data?.detail || 'Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [open, client]);

  const save = async () => {
    if (!client) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/google-ads/clients/${client.id}/offline-conversion-action`,
        { conversion_action_id: selected },
        { withCredentials: true },
      );
      toast.success('Conversion action enregistrée');
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="offline-conv-action-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Target className="w-5 h-5 text-blue-600" />
            Conversion offline Google Ads — {client?.name}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Choisir l'action de conversion qui recevra les ventes signées (status "won" + gclid).
            <strong className="text-amber-700"> L'action doit être de type "Importer des clics"</strong> dans Google Ads.
            Si vous n'en avez pas, créez-en une dans Google Ads → Outils → Conversions.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center p-6"><Loader2 className="w-5 h-5 animate-spin text-neutral-400" /></div>
        ) : error ? (
          <div className="bg-rose-50 border border-rose-200 rounded p-3 text-sm text-rose-700">{error}</div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Action de conversion ({actions.length} disponible{actions.length > 1 ? 's' : ''})</Label>
              {actions.length === 0 ? (
                <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-800">
                  Aucune action de type <code>UPLOAD_CLICKS</code> trouvée pour ce compte. Créez-en une dans Google Ads d'abord.
                </div>
              ) : (
                <Select value={selected} onValueChange={setSelected}>
                  <SelectTrigger><SelectValue placeholder="Choisir une action..." /></SelectTrigger>
                  <SelectContent>
                    {actions.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name} <span className="text-neutral-400 text-xs ml-1">· {a.category}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {selected && (
              <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Les ventes signées avec gclid seront poussées à cette action.
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
              <Button
                onClick={save}
                disabled={saving || !actions.length}
                className="bg-[#002FA7] hover:bg-[#002277]"
                data-testid="save-offline-action"
              >
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
